import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { DetroitGameState } from '../../types';

/**
 * Phase 10: ターン進行フェーズ（Detroit固有）
 *
 * ターンフラグをリセットし、次のターンに進みます。
 * ロジック処理後、ユーザーがクリックするまで待機します。
 * Detroit: ターン制限なし（破産まで継続）
 *
 * 参照:
 * - docs/specifications/logic/detroit_bankruptcy_solo/phase_10_advance_turn_override.md
 */
export const advanceTurnDetroitPhase: PhaseConfig<DetroitGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx }: { G: DetroitGameState; ctx: any }) => {
    debug.log(`[advanceTurnDetroitPhase] ★★★ Phase 10: Advance Turn started ★★★`);
    debug.log(`[advanceTurnDetroitPhase] ★★★ ctx:`, { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ターン番号はPhase 1 (issueShares) でインクリメントされる
    const currentActualTurn = G.actualTurn || 1;
    debug.log(`[advanceTurnDetroitPhase] Current actualTurn: ${currentActualTurn}`);

    // Detroit: ターン制限なし（破産まで継続）、ゲーム終了判定は破産時のみ
    debug.log(`[advanceTurnDetroitPhase] Game continues (no turn limit in Detroit)`);

    // 全プレイヤーのターンフラグをリセット
    for (const playerId of ctx.playOrder) {
      const player = G.players[playerId];

      // ターンフラグリセット
      player.selectedAction = null;
      player.tilesPlacedThisTurn = 0;
      player.newCityTilesPlacedThisTurn = 0;
      player.hasPassedBuild = false;
      player.hasUpgradedLocoThisTurn = false;
      player.goodsPlacedThisTurn = 0;
      player.orderBidAmount = 0;

      // Phase 5 状態リセット
      player.turnsCompletedThisPhase = 0;
      player.goodsTransportedThisPhase = 0;

      // Detroit専用: フラグリセット
      player.hasExecutedIssueSharesMove = false;
      player.hasPassed = false;
    }

    // 処理結果を保存（UI表示用）- 次ターン番号を表示
    const nextTurn = currentActualTurn + 1;
    debug.log(`[advanceTurnDetroitPhase] Turn flags reset, ready for turn ${nextTurn}`);

    G.autoPhaseResult = {
      phase: 'advanceTurn',
      message: 'Advancing to next turn...',
      floatingText: `Turn ${nextTurn}`,
      details: { newTurn: nextTurn },
    };

    debug.log('[advanceTurnDetroitPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: DetroitGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
  },

  onEnd: ({ G, ctx }: { G: DetroitGameState; ctx: any }) => {
    debug.log('[advanceTurnDetroitPhase] ★★★ onEnd called - Phase 10 ended ★★★');
    debug.log('[advanceTurnDetroitPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log(`[advanceTurnDetroitPhase] ★★★ actualTurn: ${G.actualTurn}`);
    debug.log('[advanceTurnDetroitPhase] ★★★ Next phase should be: issueShares');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('advanceTurn');
  },

  // 次フェーズへ遷移（Phase 1に戻る）
  next: 'issueShares'
};
